import { BemHelper } from "@/utils/BemHelper";
import classNames from "classnames";
import React from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Theme, ThemeButtonSize, ThemeFontVariant, ThemeIntent } from "../types";
import { THEMES, THEME_BUTTON_SIZES, THEME_FONT_VARIANTS, THEME_INTENTS } from "../constants";

export type CtButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    children: React.ReactNode;
    className?: string;
    onClick?: () => void;
    disabled?: boolean;
    loading?: boolean;
    outlined?: boolean;
    minimal?: boolean;
    intent: ThemeIntent;
    size?: ThemeButtonSize;
    theme?: Theme;
    fontVariant?: ThemeFontVariant;
    disablePadding?: boolean;
  };

const bem = new BemHelper("ct-button");

function CtButton({
  children,
  className,
  onClick,
  disabled,
  loading,
  outlined,
  minimal,
  intent,
  size,
  theme,
  href,
  fontVariant,
  disablePadding,
  ...props
}: CtButtonProps) {
  const isAnchor = !!href;
  const Component = isAnchor ? "a" : "button";
  return (
    <Component
      className={classNames(
        bem.block(),
        bem.modifier(theme || THEMES.THEME_1),
        bem.modifier(intent || THEME_INTENTS.DEFAULT),
        bem.modifier(size || THEME_BUTTON_SIZES.MEDIUM),
        bem.modifier(fontVariant || THEME_FONT_VARIANTS.FONT_VARIANT_1),
        disabled && bem.modifier("disabled"),
        loading && bem.modifier("loading"),
        outlined && bem.modifier("outlined"),
        minimal && bem.modifier("minimal"),
        disablePadding && bem.modifier("disable-padding"),
        className,
      )}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <div className={classNames(bem.element("loading-container"))}>
          <LoadingOutlined spin style={{ fontSize: 24 }} />
        </div>
      )}
      {children}
    </Component>
  );
}

CtButton.defaultProps = {
  intent: THEME_INTENTS.DEFAULT,
  size: THEME_BUTTON_SIZES.MEDIUM,
  disabled: false,
  loading: false,
  outlined: false,
  minimal: false,
  theme: THEMES.THEME_1,
  fontVariant: THEME_FONT_VARIANTS.FONT_VARIANT_1,
  disablePadding: false,
};

export default CtButton;
